import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge, Button, type BadgeTone } from '@artosera/ui';
import {
  importEnCours,
  type EtatImport,
  type ImportConnexion,
} from '../../../../lib/connecteurs.js';
import { formaterDate, formaterOctets } from '../../../../lib/format.js';
import reprise from '../../../tabs/reprise/Reprise.module.css';
import styles from './Connecteur.module.css';

const TON_IMPORT: Record<EtatImport, BadgeTone> = {
  en_attente: 'reserved',
  en_cours: 'reserved',
  termine: 'available',
  plafond_atteint: 'deposit',
  abandonne: 'sold',
  interrompu: 'sold',
};

/** La phrase d'un arrêt : la raison nommée, sinon le code tel quel. */
function phraseRaison(t: ReturnType<typeof useTranslation>['t'], raison: string): string {
  const nommee = String(t(`importDropbox.suivi.raisons.${raison}`, { defaultValue: '' }));
  return nommee !== '' ? nommee : String(t('importDropbox.suivi.raisons.brute', { code: raison }));
}

/**
 * Où en est l'import : ses compteurs, relus toutes les 3 secondes tant qu'il
 * tourne, puis le chemin vers le dépôt qu'il a rempli. L'API ne donne pas de
 * total à l'avance : la barre est indéterminée.
 */
export function SuiviImport({
  idGalerie,
  lancement,
  peutRelancer,
  relancement,
  onRelancer,
}: {
  idGalerie: string;
  lancement: ImportConnexion;
  /** Vrai quand la connexion est autorisée et le dossier ouvert. */
  peutRelancer: boolean;
  /** Le relancement est en cours d'envoi. */
  relancement: boolean;
  onRelancer: () => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const vivant = importEnCours(lancement);
  const fini = lancement.etat === 'termine' || lancement.etat === 'plafond_atteint';
  const compteurs: [keyof ImportConnexion, string][] = [
    ['fichiers', String(lancement.fichiers)],
    ['octets', formaterOctets(lancement.octets)],
    ['dejaPresents', String(lancement.dejaPresents)],
    ['ecartes', String(lancement.ecartes)],
    ['enregistrements', String(lancement.enregistrements)],
  ];

  return (
    <div className={styles.suivi} data-etat={lancement.etat}>
      <div className={reprise.rangee}>
        <Badge tone={TON_IMPORT[lancement.etat]}>
          {t(`importDropbox.suivi.etats.${lancement.etat}`)}
        </Badge>
        <span className={reprise.discret}>
          {lancement.termineLe !== null
            ? t('importDropbox.suivi.termine', { date: formaterDate(lancement.termineLe, '—') })
            : t('importDropbox.suivi.lance', { date: formaterDate(lancement.lanceLe, '—') })}
        </span>
      </div>

      {vivant ? (
        <progress className={styles.progression} aria-label={t('importDropbox.suivi.progression')}>
          {t('importDropbox.suivi.progression')}
        </progress>
      ) : null}

      <dl className={styles.compteursImport}>
        {compteurs.map(([cle, valeur]) => (
          <div key={cle}>
            <dt>{t(`importDropbox.suivi.compteurs.${cle}`)}</dt>
            <dd>{valeur}</dd>
          </div>
        ))}
      </dl>

      {lancement.etat === 'en_attente' ? (
        <p className={reprise.texte}>{t('importDropbox.suivi.enAttente')}</p>
      ) : null}
      {lancement.etat === 'en_cours' ? (
        <p className={reprise.texte} role="status">
          {t('importDropbox.suivi.enCours')}
        </p>
      ) : null}
      {lancement.etat === 'termine' ? (
        <p className={reprise.texte}>{t('importDropbox.suivi.fini')}</p>
      ) : null}
      {lancement.etat === 'plafond_atteint' ? (
        <p className={reprise.avertissement}>{t('importDropbox.suivi.pleinDepot')}</p>
      ) : null}
      {lancement.etat === 'interrompu' ? (
        <p className={reprise.erreur} role="alert">
          {t('importDropbox.suivi.interrompu')}
        </p>
      ) : null}
      {lancement.etat === 'abandonne' ? (
        <p className={reprise.erreur} role="alert">
          {t('importDropbox.suivi.abandonne')}
        </p>
      ) : null}
      {lancement.raison !== null && lancement.etat !== 'termine' ? (
        <p className={reprise.discret}>{phraseRaison(t, lancement.raison)}</p>
      ) : null}

      <div className={reprise.actions}>
        {fini || lancement.etat === 'abandonne' || lancement.etat === 'interrompu' ? (
          <Link
            to={`/galeries/${idGalerie}/reprise#depot-${lancement.depotId}`}
            className={styles.lienBouton}
          >
            {t('importDropbox.suivi.versDepot')}
          </Link>
        ) : null}
        {peutRelancer && !vivant ? (
          <Button variant="secondary" loading={relancement} onClick={onRelancer}>
            {lancement.etat === 'interrompu'
              ? t('importDropbox.lancer.reprendre')
              : t('importDropbox.lancer.relancer')}
          </Button>
        ) : null}
      </div>
      {fini ? <p className={reprise.discret}>{t('importDropbox.suivi.versDepotAide')}</p> : null}
    </div>
  );
}
